/* NPM */
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
/* COMPONENTS */
import { Checkbox, Flex } from '@solinkcorp/solink-components';
import CloudStatus from '../../CloudStatus/CloudStatus';
import ReceiptPopover from './ReceiptPopover';
/* UTILS */
import * as DateHandler from 'utils/DateHandler';
import LocaleUtil from 'utils/LocaleUtil';
import TimeFormatter from 'utils/TimeFormatter';
/* CONSTANTS */
import { EVENT_TYPE_BOOKMARK, EVENT_TYPE_TRANSACTION } from '@solinkcorp/vd-common';
import {
  type CLOUD_STATUS_COMPLETE_WITH_ERRORS,
  CONTEXT_DOWNLOAD,
  DEFAULT_TIMEZONE,
  type EventContextType,
  type PushToCloudStatus,
  SHARE_STATUS_DISABLED,
  type VISIBILITY_TYPE,
} from 'vd-constants';
/* STYLES */
import './SavedDetails.sass';
import styles from './SaveDetails.styles';
/* HOOKS */
import { type IEvent, type Share } from 'clients/PlatformApi';
import useLocale from 'src/hooks/useLocale';
import { type ModifiedShare } from '../types';
import { getShareAccessStatusText } from '../utils/shareAccessStatusUtils';
import SharedEmail from './SharedEmail';

interface SavedDetailsProps {
  canShare: boolean;
  cloudStatusError?: boolean;
  contextMode: EventContextType;
  displayCloudStatus?: boolean;
  displayFillGaps?: boolean;
  enterEditMode?: () => void;
  errorEmail?: string;
  event: IEvent;
  fillGaps?: boolean;
  ifShare: boolean | undefined;
  isBestQuality?: boolean;
  isInProgress?: boolean;
  isLoading?: boolean;
  onChange?: (fieldName: string, value: boolean) => void;
  pollingState?: PushToCloudStatus | typeof CLOUD_STATUS_COMPLETE_WITH_ERRORS | null;
  selfShare?: Partial<ModifiedShare>;
  selfSharePolling?: boolean;
  shareMessage?: string;
  shares?: Share[];
  tenantId: string;
  timezoneId?: string;
  visibility?: VISIBILITY_TYPE;
  faceblur?: boolean;
}

const SavedDetails: React.FC<SavedDetailsProps> = (props) => {
  const {
    canShare,
    cloudStatusError,
    contextMode,
    displayCloudStatus,
    displayFillGaps,
    enterEditMode,
    errorEmail,
    event,
    fillGaps,
    ifShare,
    isInProgress,
    isLoading,
    pollingState,
    onChange,
    shareMessage,
    shares,
    tenantId,
    timezoneId = DEFAULT_TIMEZONE,
    visibility,
    faceblur,
  } = props;

  const {
    title,
    type: eventType,
    shareStartTime,
    shareEndTime,
    startTime,
    endTime,
    details,
    pushToCloudStatus,
    locationId,
  } = event;

  const locale = useLocale();

  const handleIfShareChange = ({ value }: { value: boolean }) => {
    if (onChange) onChange('ifShare', value);
    if (enterEditMode) enterEditMode();
  };

  const isContextDownload = contextMode === CONTEXT_DOWNLOAD;
  const savedStartTime = shareStartTime || startTime;
  const savedEndTime = shareEndTime || endTime;

  let totalViews = 0;
  if (displayCloudStatus) {
    const originalShares = event.shares ?? [];
    originalShares.forEach((share) => {
      totalViews += share.views ? share.views.length : 0;
    });
  }

  const recipientWrapperClass = classNames('recipient-wrapper', {
    disabled: !ifShare,
  });
  const shareMessageWrapperClass = classNames('share-message-wrapper', 'multiline-text', {
    disabled: !ifShare,
  });
  const clipTitleClass = classNames('details-title', {
    'existing-event-title': eventType !== EVENT_TYPE_BOOKMARK,
  });

  return (
    <div className="save-details-wrapper">
      <div>
        <div className="details-item_wrapper">
          <div className={clipTitleClass}>{locale.fields.title}</div>
          <div className={`details-content ${eventType}`}>
            <span className="text">{title}</span>
            {eventType === EVENT_TYPE_TRANSACTION && <ReceiptPopover receipt={details?.Receipt} />}
          </div>
        </div>
        <Flex className="details-item_wrapper full-width">
          <div className="details-item">
            <div className="details-title">{locale.fields.start}</div>
            <div className="details-content">
              {moment(savedStartTime)
                .tz(timezoneId || DEFAULT_TIMEZONE)
                .format(`${TimeFormatter.getString()} - LL`)}
            </div>
          </div>
          <div className="details-item">
            <div className="details-title">{locale.fields.end}</div>
            <div className="details-content">
              {moment(savedEndTime)
                .tz(timezoneId || DEFAULT_TIMEZONE)
                .format(`${TimeFormatter.getString()} - LL`)}
            </div>
          </div>
          <div className="details-item">
            <div className="details-title">{locale.cloudShare.duration}</div>
            <div className="details-content">
              {DateHandler.getDuration(locale, savedStartTime, savedEndTime)}
            </div>
          </div>
          {!isContextDownload && (
            <div className="details-item">
              <div className="details-title">{locale.general.status}</div>
              <Flex className="details-content cloud-status-wrapper">
                <CloudStatus
                  eventStatus={pushToCloudStatus}
                  pollingStatus={pollingState}
                  isInProgress={isInProgress}
                  locationId={locationId}
                  event={event}
                  tenantId={tenantId}
                />
                {totalViews > 0 && (
                  <Flex className="cloud-views">
                    {LocaleUtil.getLocalizedStr({
                      string: locale.cloudShare.viewCount,
                      replacements: { count: totalViews },
                    })}
                  </Flex>
                )}
              </Flex>
            </div>
          )}
        </Flex>
        {displayFillGaps && (
          <div className="details-item_wrapper">
            <div className="details-title">{locale.cloudShare.downloadFillingGaps}</div>
            <div className="details-content capitalize">
              {fillGaps ? locale.general.yes : locale.general.no}
            </div>
          </div>
        )}
        {!isContextDownload && (
          <div className="details-item_wrapper">
            <div className="details-title">{locale.dashboard.visibility}</div>
            <div className="details-content capitalize">{visibility}</div>
          </div>
        )}
        {details?.description && (
          <div className="details-item_wrapper">
            <div className="details-title">{locale.fields.notes}</div>
            <div className="details-content">{details.description}</div>
          </div>
        )}
        <div className="details-item_wrapper">
          <div className="details-title">{locale.faceblur.blurred}</div>
          <div className="details-content capitalize">
            {faceblur ? locale.general.yes : locale.general.no}
          </div>
        </div>
      </div>
      {(canShare || ifShare) && (
        <div className="details-item_wrapper full-width">
          {ifShare && <div className="details-title">{locale.cloudShare.shareDetails}</div>}
          <div className="email-wrapper">
            {!ifShare && !isLoading && (
              <Checkbox
                checked={ifShare}
                disabled={contextMode === CONTEXT_DOWNLOAD || isInProgress || cloudStatusError}
                labelText={locale.cloudShare.shareWithSomeone}
                name="if-share"
                onChange={handleIfShareChange}
                style={cloudStatusError ? styles.checkboxWrapperDisable : styles.checkboxWrapper}
              />
            )}
            <div className={recipientWrapperClass}>
              <Flex className="share-panel" justifyContent="space-between" alignItems="center">
                <Flex className="recipient-list" alignItems="center">
                  {shares?.map((share, idx) => {
                    const isNewShare = !share.id;

                    // Don't render originator (user who initiated the share)
                    if (share.sharedBy === share.email) {
                      return <div key={idx}></div>;
                    }
                    const accesses = share.views || [];

                    const accessStatusClass = classNames({
                      seen: accesses.length > 0,
                      notified: !!share.notified,
                    });

                    const recipientClass = classNames('recipient', 'readonly', {
                      disabled: share.status === SHARE_STATUS_DISABLED,
                      new: isNewShare,
                    });

                    const accessStatusTooltip = getShareAccessStatusText(share, locale);

                    if (share.email.length) {
                      return (
                        <SharedEmail
                          key={idx}
                          accessStatusTooltip={accessStatusTooltip}
                          accessStatusClass={accessStatusClass}
                          shareEmail={share.email}
                          recipientClass={recipientClass}
                        />
                      );
                    }
                  })}
                  {!ifShare && <span className="recipients">{locale.cloudShare.recipients}</span>}
                </Flex>
              </Flex>
              <div className="email-error-message">{errorEmail}</div>
            </div>
            <Flex flexDirection="column" className={shareMessageWrapperClass}>
              <Flex flexDirection="column" style={styles.shareMessage}>
                {shareMessage || `${locale.fields.emailMessage} (optional)`}
              </Flex>
            </Flex>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedDetails;
